import { ChangeDetectorRef, Component, Signal, WritableSignal, inject, signal } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogActions,
  MatDialogModule,
  MatDialogTitle,
} from '@angular/material/dialog';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import OrganizationService from 'src/app/shared/services/organization.service';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { Organization } from '../../organization/organization-models';
import { combineLatest, debounceTime, defer, distinctUntilChanged, EMPTY, firstValueFrom, iif, of, startWith, switchMap, tap } from 'rxjs';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { ConnectorSelectorComponent } from 'src/app/shared/components/connector-selector/connector-selector.component';
import { AccessGroup, CreateAccessGroup } from '../access-group.model';
import { createMask, InputMaskModule } from '@ngneat/input-mask';
import { ConnectorService } from 'src/app/shared/services/connector.service';
import { ConnectorLocationResponse, ConnectorSearchEvent } from '../../charging-station-location/charging-station-location-model';
import { TranslateOrganizationDiscountTypeEnum } from '../../../shared/pipes/translate-organization-discount-type-enum.pipe';
import { EnumToArrayPipe } from 'src/app/shared/pipes/enum-to-array.pipe';
import { TranslateAccessGroupCarTypeEnum } from 'src/app/shared/pipes/translate-access-group-car-type-enum.pipe';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import {
  AccessGroupSharedDiscountService,
  IAccessGroupSharedDiscountSearchResultItem,
} from 'src/app/shared/services/access-group-shared-discount.service';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSelect } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatBadge } from '@angular/material/badge';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';

interface AccessGroupFormControls {
  identifier: FormControl<string | null>;
  name: FormControl<string | null>;
  showDiscountFields: FormControl<boolean>;
  sharedDiscountIdentifier: FormControl<string | null>;
  organizationId: FormControl<number | null>;
  organization: FormControl<Organization | null>;
  amount: FormControl<number | null>;
  discountType: FormControl<DiscountType>;
  connectorIdentifiers: FormControl<string[]>;
  carType: FormControl<AccessGroupCarType>;
}

@Component({
  selector: 'adm-access-group-create-or-edit-modal',
  templateUrl: './access-group-create-or-edit-modal.component.html',
  styleUrls: ['./access-group-create-or-edit-modal.component.scss'],
  imports: [
    CustomDropDownArrowDirective,
    InputMaskModule,
    TranslateOrganizationDiscountTypeEnum,
    EnumToArrayPipe,
    TranslateAccessGroupCarTypeEnum,
    FeedbackInfoComponent,
    MatIcon,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatError,
    MatSlideToggle,
    MatHint,
    MatSelect,
    MatDialogActions,
    MatBadge,
    ReactiveFormsModule,
    CommonModule,
    MatAutocompleteModule,
    MatButton,
    MatDialogModule,
    AdmOrgTypeaheadComponent,
  ],
})
export class AccessGroupCreateOrEditModalComponent {
  dialogRef = inject<MatDialogRef<AccessGroupCreateOrEditModalComponent>>(MatDialogRef);
  data = inject<{
    accessGroupIdentifier: string;
  }>(MAT_DIALOG_DATA);
  private accessGroupService = inject(AccessGroupService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private connectorService = inject(ConnectorService);
  private sharedDiscountService = inject(AccessGroupSharedDiscountService);
  private organizationService = inject(OrganizationService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  public carTypes: typeof AccessGroupCarType = AccessGroupCarType;
  public accessGroupId: number | null = null;
  public accessGroupIdentifier: string | null = this.data?.accessGroupIdentifier;
  public selectedOrganization: Organization;
  public selectedConnectorIdentifiers: string[];
  public connectorBadgeCount = 0;
  public connectorSearchEvent: ConnectorSearchEvent = {} as ConnectorSearchEvent;
  public connectorsWithPrices: ConnectorLocationResponse[] = [];
  public discountPreviews: DiscountPreview[] = [];
  public calculating = false;
  public accessGroupForm: FormGroup<AccessGroupFormControls> = new FormGroup<AccessGroupFormControls>({
    identifier: new FormControl<string | null>(null),
    name: new FormControl(null, [Validators.required]),
    showDiscountFields: new FormControl(false, { nonNullable: true }),
    sharedDiscountIdentifier: new FormControl(null),
    organizationId: new FormControl<number | null>(null, [Validators.required]),
    amount: new FormControl<number | null>(null, [Validators.min(0), Validators.max(100)]),
    discountType: new FormControl<DiscountType>({ value: DiscountType.FixedAmount, disabled: true }, { nonNullable: true }),
    connectorIdentifiers: new FormControl<string[]>([], { nonNullable: true }),
    organization: new FormControl<Organization | null>(null),
    carType: new FormControl<AccessGroupCarType>(
      { value: AccessGroupCarType.CompanyCar, disabled: this.accessGroupIdentifier !== undefined },
      { validators: [Validators.required], nonNullable: true }
    ),
  });

  get name() {
    return this.accessGroupForm.controls.name;
  }
  get connectorIdentifiers() {
    return this.accessGroupForm.controls.connectorIdentifiers;
  }
  get identifier() {
    return this.accessGroupForm.controls.identifier;
  }
  get organizationId() {
    return this.accessGroupForm.controls.organizationId;
  }
  get amount() {
    return this.accessGroupForm.controls.amount;
  }
  get sharedDiscountIdentifier() {
    return this.accessGroupForm.controls.sharedDiscountIdentifier;
  }
  get discountType() {
    return this.accessGroupForm.controls.discountType;
  }
  get showDiscountFields() {
    return this.accessGroupForm.controls.showDiscountFields;
  }

  priceInputMask = createMask({
    alias: 'numeric',
    placeholder: '',
    digits: 2,
    min: 0,
    groupSeparator: '',
    radixPoint: ',',
  });

  public discountTypes: typeof DiscountType = DiscountType;
  public sharedDiscounts: IAccessGroupSharedDiscountSearchResultItem[] = [];

  private accessGroup$: Signal<AccessGroup | undefined> = toSignal(
    defer(() =>
      iif(() => !!this.data?.accessGroupIdentifier, this.accessGroupService.getAccessGroup(this.data.accessGroupIdentifier), EMPTY)
    ).pipe(
      tap(accessGroup => {
        if (accessGroup) {
          const carType = accessGroup.carType ? accessGroup.carType : AccessGroupCarType.CompanyCar;

          this.accessGroupForm.patchValue({
            identifier: accessGroup.identifier,
            name: accessGroup.name,
            showDiscountFields: accessGroup?.sharedDiscountIdentifier || accessGroup?.amount ? true : false,
            sharedDiscountIdentifier: accessGroup.sharedDiscountIdentifier,
            organizationId: accessGroup.organizationId,
            amount: accessGroup.amount,
            discountType: accessGroup.discountType,
            connectorIdentifiers: accessGroup.connectorIdentifiers,
            carType: carType,
          });
          this.accessGroupForm.controls.carType.disable();

          if (accessGroup.sharedDiscountIdentifier) {
            this.amount.disable({ emitEvent: false });
            this.amount.setValue(null);
          } else if (accessGroup.amount) {
            this.sharedDiscountIdentifier.disable({ emitEvent: false });
          }

          this.accessGroupForm.markAllAsTouched();

          this.accessGroupId = accessGroup.id;
          this.connectorBadgeCount = accessGroup.connectorIdentifiers.length;
          this.selectedConnectorIdentifiers = accessGroup.connectorIdentifiers;
          this.organizationService
            .getOrganization(accessGroup.organizationId)
            .subscribe(organization => this.organizationSelected(organization));
        }
      }),
      takeUntilDestroyed()
    )
  );

  clearOrganizationSelector() {
    this.resetOrgTrigger.set({ reset: true });
  }

  organizationSelected = (org?: Organization | null) => {
    if (this.accessGroupForm.controls.organization.value?.id === org?.id) {
      return;
    }

    this.accessGroupForm.patchValue({
      organizationId: org ? org.id : null,
      organization: org ? org : null,
    });
  };

  private organizationChanged = this.accessGroupForm.controls.organization.valueChanges
    .pipe(
      switchMap(organization => {
        if (organization) {
          this.showDiscountFields.enable();

          return this.sharedDiscountService.getSharedDiscounts({ discountType: organization.discountType } as any).pipe(
            tap(result => {
              this.sharedDiscounts = result.items || [];
              this.changeDetectorRef.detectChanges();
            }),
            tap(() => {
              if (!this.data?.accessGroupIdentifier) {
                this.discountType.setValue(organization.discountType);
              }
            })
          );
        } else {
          this.showDiscountFields.disable();
          this.showDiscountFields.setValue(false);
          this.sharedDiscounts = [];
        }
        return of(organization);
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  private formChanged = combineLatest([
    this.sharedDiscountIdentifier.valueChanges.pipe(startWith(this.sharedDiscountIdentifier.value), distinctUntilChanged()),
    this.amount.valueChanges.pipe(startWith(this.amount.value), distinctUntilChanged()),
    this.connectorIdentifiers.valueChanges.pipe(
      startWith(this.connectorIdentifiers.value),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ),
    this.showDiscountFields.valueChanges.pipe(startWith(this.showDiscountFields.value), distinctUntilChanged()),
    this.discountType.valueChanges.pipe(startWith(this.discountType.value), distinctUntilChanged()),
  ])
    .pipe(
      debounceTime(100),
      tap(async ([sharedDiscountIdentifier, amount, connectorIdentifiers, showDiscountFields]) => {
        this.calculating = true;
        const amountControl = this.amount;
        if (sharedDiscountIdentifier && amountControl.enabled) {
          amountControl.disable({ emitEvent: false });
        } else if (!sharedDiscountIdentifier && amountControl.disabled) {
          amountControl.enable({ emitEvent: false });
        }

        if (!amount) {
          this.amount.setValue(null, { emitEvent: false });
        }
        const sharedDiscountIdentifierControl = this.sharedDiscountIdentifier;
        if (amount && sharedDiscountIdentifierControl.enabled) {
          sharedDiscountIdentifierControl.disable({ emitEvent: false });
        } else if (!amount && sharedDiscountIdentifierControl.disabled) {
          sharedDiscountIdentifierControl.enable({ emitEvent: false });
        }

        this.selectedConnectorIdentifiers = connectorIdentifiers;

        if (!showDiscountFields) {
          this.sharedDiscountIdentifier.setValue(null, { emitEvent: false });
          this.amount.setValue(null, { emitEvent: false });
        }
      }),
      tap(() => {
        setTimeout(async () => {
          await this.populateDiscountPreviews();
          this.calculating = false;
        }, 400);
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  async saveAccessGroup(): Promise<void> {
    if (this.amount.value) {
      this.amount.setValue(this.parsePrice(this.amount.value));
    }
    if (this.sharedDiscountIdentifier.value) {
      this.sharedDiscountIdentifier.setValue(this.sharedDiscountIdentifier.value);
    }
    try {
      const rawForm = this.accessGroupForm.getRawValue();
      if (rawForm.organizationId == null || rawForm.name == null) {
        return;
      }

      const request: CreateAccessGroup = {
        identifier: rawForm.identifier ?? undefined,
        organizationId: rawForm.organizationId,
        name: rawForm.name,
        amount: rawForm.amount,
        connectorIdentifiers: rawForm.connectorIdentifiers ?? [],
        carType: rawForm.carType,
        sharedDiscountIdentifier: rawForm.sharedDiscountIdentifier ?? undefined,
      };

      const accessGroupIdentifier = await firstValueFrom(this.accessGroupService.saveAccessGroup(request));
      const accessGroup = await firstValueFrom(this.accessGroupService.getAccessGroup(accessGroupIdentifier));

      this.snackbarService.success(`Adgangsgruppen blev succesfuldt ${this.identifier.value ? 'redigeret' : 'oprettet'}`);
      this.dialogRef.close(accessGroup);
    } catch (err) {
      const errorCode = this.getErrorCode(err);
      switch (errorCode) {
        case 13100: // AccessGroupNameDuplicate
          this.snackbarService.error('Adgangsgruppe med dette navn findes allerede i denne organisation.');
          break;
        case 100104: // OrganizationNotFound
          this.snackbarService.error('Organisation blev ikke fundet.');
          break;
        case 14003: // AccessGroupPercentageError
          this.snackbarService.error('Procent-rabat er ugyldig for adgangsgruppe.');
          break;
        case 15102: // SharedDiscountNotFound
          this.snackbarService.error('Tværgående rabat ikke fundet.');
          break;
        case 15103: // SharedDiscountTypeMismatch
          this.snackbarService.error('Den tværgående rabattype matcher ikke adgangsgruppens organisations rabattype.');
          break;
        case 15104: // AccessGroupSharedDiscountAndAmount
          this.snackbarService.error('Der kan ikke både være en valgt tværgående rabat og et beløb for adgangsgruppen.');
          break;
        default:
          this.snackbarService.error(`Der skete en ukendt fejl under ${this.identifier.value ? 'redigering' : 'oprettelse'}`);
      }
    }
  }

  private parsePrice(price: number): number {
    const parsedPrice = Number(price.toString().replace(',', '.'));
    return parsedPrice;
  }

  private getErrorCode(err: unknown): number | string | undefined {
    if (!err || typeof err !== 'object') {
      return undefined;
    }

    const maybeError = err as { error?: { code?: number | string } };
    return maybeError.error?.code;
  }

  public resetLocations() {
    this.selectedConnectorIdentifiers = [];
    this.connectorIdentifiers.setValue([]);
    this.connectorBadgeCount = 0;
  }
  public openConnectorSelector() {
    if (!this.organizationId.value) {
      return;
    }
    const dialogRef = this.dialog.open(ConnectorSelectorComponent, {
      data: {
        organizationId: this.organizationId.value,
        selectedConnectorIdentifiers: this.connectorIdentifiers.value,
      },
      width: '850px',
    });

    dialogRef.afterClosed().subscribe((selectedConnectorIdentifiers: string[]) => {
      if (selectedConnectorIdentifiers) {
        this.connectorBadgeCount = selectedConnectorIdentifiers.length;
        this.connectorIdentifiers.setValue(selectedConnectorIdentifiers);
      }
    });
  }

  private async populateDiscountPreviews() {
    this.connectorSearchEvent.organizationId = this.organizationId.value ?? undefined;
    if (
      this.connectorSearchEvent.organizationId === null ||
      !this.selectedConnectorIdentifiers ||
      this.selectedConnectorIdentifiers.length === 0
    ) {
      this.discountPreviews = [];
      return;
    }
    if (this.connectorsWithPrices.length === 0) {
      this.connectorsWithPrices = await firstValueFrom(this.connectorService.getConnectorsOnOrganization(this.connectorSearchEvent));
    }

    const connectorsWithPrices = this.connectorsWithPrices?.filter(c =>
      c.connectors.some(co => this.selectedConnectorIdentifiers.indexOf(co.connectorIdentifier) > -1)
    );

    const pricePolicyNames = connectorsWithPrices.flatMap(c =>
      c.connectors.filter(co => this.selectedConnectorIdentifiers.indexOf(co.connectorIdentifier) > -1).flatMap(co => co.pricePolicyName)
    );

    this.discountPreviews = this.discountPreviews.filter(dp => pricePolicyNames.indexOf(dp.pricePolicyName) > -1);
    connectorsWithPrices.forEach(conn => {
      const selectedConnectors = conn.connectors.filter(co => this.selectedConnectorIdentifiers.indexOf(co.connectorIdentifier) > -1);
      selectedConnectors.forEach(selectedConnector => {
        const discountPreviewIndex = this.discountPreviews.findIndex(dp => dp.pricePolicyName === selectedConnector.pricePolicyName);
        if (discountPreviewIndex === -1) {
          this.discountPreviews.push({
            pricePolicyName: selectedConnector.pricePolicyName,
            origPricePerUnit: selectedConnector.consumerPricePerKwh,
            discountedPricePerUnit: this.calculateDiscountedPriceInclVat(selectedConnector.consumerPricePerKwh),
          } as DiscountPreview);
        } else {
          this.discountPreviews[discountPreviewIndex].origPricePerUnit = selectedConnector.consumerPricePerKwh;
          this.discountPreviews[discountPreviewIndex].discountedPricePerUnit = this.calculateDiscountedPriceInclVat(
            selectedConnector.consumerPricePerKwh
          );
        }
      });
    });
  }

  /**
   * Calculates the discounted price per unit incl. VAT.
   *
   * @param pricePerUnitInclVat - The consumer price per kWh including VAT.
   *
   * For FixedAmount discounts the stored discountAmount is ex. VAT, so VAT is added, before being subtracted from the incl-VAT price.
   * For Percentage discounts the percentage is applied directly to the incl-VAT price.
   */
  calculateDiscountedPriceInclVat(pricePerUnitInclVat: number): number {
    const discountType = this.discountType.value;
    let discountAmountExclVat = this.amount.value;
    if (this.sharedDiscountIdentifier.value) {
      const sharedDiscount = this.sharedDiscounts.find(d => d.identifier === this.sharedDiscountIdentifier.value);
      discountAmountExclVat = sharedDiscount?.amount || 0;
    }

    let discountedPriceInclVat = pricePerUnitInclVat;
    if (discountType && discountAmountExclVat) {
      discountAmountExclVat = parseFloat(discountAmountExclVat.toString().replace(',', '.'));
      if (discountType === DiscountType.Percentages) {
        // Percentage is applied to the incl-VAT price directly.
        discountedPriceInclVat = pricePerUnitInclVat - (pricePerUnitInclVat * discountAmountExclVat) / 100;
      } else {
        // FixedAmount is stored ex. VAT — convert to incl. VAT before subtracting.
        const discountAmountInclVat = discountAmountExclVat * 1.25;
        discountedPriceInclVat = pricePerUnitInclVat - discountAmountInclVat;
      }
      if (discountedPriceInclVat < 0) {
        discountedPriceInclVat = 0;
      }
    }
    return discountedPriceInclVat;
  }
}

export interface DiscountPreview {
  pricePolicyName: string;
  origPricePerUnit: number;
  discountedPricePerUnit: number;
}
